import { Injectable, effect, signal } from '@angular/core';
import type { Observable } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { loadSession, saveSession } from '../../session-sync/utils/session-storage.util';
import type { IAuthStateService } from '../interfaces/auth-core.interfaces';
import { AuthSessionStatus, type AuthenticationSession } from '../models/auth-session.model';

const DEFAULT_SESSION: AuthenticationSession = {
  status: AuthSessionStatus.SignedOut,
  subjectId: '',
  issuedAt: '',
  expiresAt: '',
  lastActivityAt: '',
  idleWarningAt: null,
  graceEndsAt: null,
};

@Injectable({ providedIn: 'root' })
/**
 * Source of truth for authentication session state.
 *
 * Exposes session state as both a readonly `Signal` (for template / computed use) and a backward-compatible
 * `Observable` stream. Persists every update to sessionStorage for cross-tab resilience.
 *
 * Note: This service has no dependencies and uses pure signal-based state management.
 */
export class AuthStateService implements IAuthStateService {
  private readonly _session = signal<AuthenticationSession>(loadSession() ?? DEFAULT_SESSION);
  private readonly _sessionSubject = new BehaviorSubject<AuthenticationSession>(this._session());

  /** Readonly signal — use in templates and `computed()` derivations. */
  readonly session = this._session.asReadonly();

  /** Observable stream for legacy RxJS consumers. */
  readonly session$: Observable<AuthenticationSession> = this._sessionSubject.asObservable();

  // Set up effect to emit on observable when signal changes
  private readonly _sessionEffect = effect(() => {
    this._sessionSubject.next(this._session());
  });

  /** Returns the latest in-memory session snapshot synchronously. */
  getSession(): AuthenticationSession {
    return this._session();
  }

  /** Merges partial updates, writes the new state, and persists to storage. */
  updateSession(partial: Partial<AuthenticationSession>): void {
    const updated = { ...this._session(), ...partial };
    this._session.set(updated);
    saveSession(updated);
  }
}
